/**
 * SendToSymfoniaButton.tsx — „Wyślij do Symfonii" na zakładce Dekretacja faktury.
 *
 * Przebieg jest DWUKROKOWY i to nie jest ozdoba: **Symfonia nie pozwala ani zmienić, ani
 * usunąć wysłanego dokumentu przez WebAPI**. Dlatego klik nigdy nie wysyła od razu:
 *   1. sprawdzenie na próbę (`dryRun: true`) — Symfonia zwraca swoje uwagi, nic u niej nie
 *      powstaje,
 *   2. modal pokazuje te uwagi (plus nasze własne ostrzeżenia) i dopiero jawne
 *      „Wyślij do Symfonii" wystawia dokument.
 *
 * Sprawdzenie na próbę jest po stronie Symfonii **doradcze, nie blokujące** — wysyłka
 * przechodzi także wtedy, gdy zgłosiła błędy (Symfonia przypnie je do dokumentu). Dlatego
 * decyzję zostawiamy człowiekowi, zamiast blokować przycisk automatem.
 *
 * Dokument już wysłany: przycisk zmienia się w informację „Wysłano", a ponowienie wymaga
 * zaznaczenia świadomej zgody — bez niej backend też odmówi (`allowResend`).
 *
 * Widoczność bramkuje rodzic: pokazujemy się tylko wtedy, gdy firma dokumentu ma
 * skonfigurowaną integrację z Symfonią (`invoice.symfoniaType.options` niepuste).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { AlertTriangle, CheckCircle2, Info, Send } from 'lucide-react';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { useReportRecordLock } from '@/hooks/useReportRecordLock';
import { api } from '@/lib/api';
import { localIsoDate } from '@/lib/local-date';

/** Uwaga zwrócona przez Symfonię. `Type` przychodzi liczbą: 0 info, 1 ostrzeżenie, 2 błąd. */
interface FkMessage {
  Type: string | number;
  Message: string;
}

interface OwnProblem {
  field: string;
  message: string;
}

interface SendResponse {
  kind: 'dry_run' | 'sent';
  messages?: FkMessage[];
  warnings?: OwnProblem[];
  blocking?: boolean;
  wrongYearSuspected?: boolean;
  fkDocumentId?: string | null;
  fkDocumentNumber?: string | null;
}

export interface SendToSymfoniaButtonProps {
  documentId: string | null | undefined;
  /** Ślad po wcześniejszej wysyłce — z GET faktury. */
  sentAt?: string | null;
  sentDocumentNumber?: string | null;
  /**
   * Czy pokazać obok przycisku plakietkę „Wysłano …". Domyślnie tak; strona w trybie edycji
   * wyświetla ją gdzie indziej (nagłówek formatki dekretu) przez `SymfoniaSentStatus`.
   */
  showSentStatus?: boolean;
  'data-testid'?: string;
}

/** Plakietka „Wysłano … (numer, data)" — ślad po wysyłce do Symfonii. */
export function SymfoniaSentStatus({
  sentAt,
  sentDocumentNumber,
  'data-testid': testId,
}: {
  sentAt: string;
  sentDocumentNumber?: string | null;
  'data-testid'?: string;
}) {
  const { t, i18n } = useTranslation();
  return (
    <span className="text-xs text-text-secondary" data-testid={testId}>
      {t('tenant.documents.decree.symfoniaSend.alreadySent', {
        number: sentDocumentNumber ?? '',
        date: new Date(sentAt).toLocaleString(i18n.language || 'pl'),
      })}
    </span>
  );
}

function isError(m: FkMessage): boolean {
  return m.Type === 2 || String(m.Type).toLowerCase() === 'error';
}
function isWarning(m: FkMessage): boolean {
  return m.Type === 1 || String(m.Type).toLowerCase() === 'warning';
}

function errorBody(err: unknown): Record<string, unknown> | null {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    return err.response.data as Record<string, unknown>;
  }
  return null;
}

export function SendToSymfoniaButton({
  documentId,
  sentAt,
  sentDocumentNumber,
  showSentStatus = true,
  'data-testid': testId = 'send-to-symfonia',
}: SendToSymfoniaButtonProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const reportRecordLock = useReportRecordLock();

  const [open, setOpen] = useState(false);
  const [resendAccepted, setResendAccepted] = useState(false);
  /** Uwagi Symfonii ze sprawdzenia na próbę + nasze własne ostrzeżenia i braki. */
  const [messages, setMessages] = useState<FkMessage[]>([]);
  const [ownWarnings, setOwnWarnings] = useState<OwnProblem[]>([]);
  const [ownProblems, setOwnProblems] = useState<OwnProblem[]>([]);
  const [wrongYear, setWrongYear] = useState(false);
  /**
   * Odmowa Symfonii, która NIE PRZYNIOSŁA ani jednej uwagi — czyli awaria po ich stronie
   * (HTTP 500 „An error has occurred.", błąd uwierzytelnienia, zły adres usługi), a nie
   * werdykt walidatora dokumentu. Bez tego stanu pusta lista uwag renderowała się jak
   * czysty wynik sprawdzenia („Symfonia nie ma uwag") i modal MILCZAŁ przy kolejnym
   * kliknięciu — user dostawał zieloną zachętę do nieodwracalnej operacji po nieudanym
   * wywołaniu.
   */
  const [fkFailure, setFkFailure] = useState<{ httpStatus?: number; raw?: string } | null>(null);

  const alreadySent = !!sentAt;

  function resetPanel() {
    setMessages([]);
    setOwnWarnings([]);
    setOwnProblems([]);
    setWrongYear(false);
    setFkFailure(null);
  }

  /** Wspólne mapowanie odmowy backendu na treść w modalu (braki danych) lub toast (awaria). */
  function handleFailure(err: unknown) {
    // Wysyłka zapisuje ślad na dokumencie — na czas zadania przycisku SQL na nim serwer odpowiada 409
    // `RECORD_LOCKED_BY_RUN`: komunikat z opisem zadania zamiast ogólnej awarii (modal się nie otwiera).
    if (reportRecordLock(err)) return;
    const body = errorBody(err);
    const code = typeof body?.error === 'string' ? body.error : undefined;

    if (code === 'NOT_READY_TO_SEND' && Array.isArray(body?.problems)) {
      setOwnProblems(body.problems as OwnProblem[]);
      setOpen(true);
      return;
    }
    if (code === 'FK_REJECTED') {
      const fkMessages = Array.isArray(body?.messages) ? (body.messages as FkMessage[]) : [];
      setMessages(fkMessages);
      setWrongYear(body?.wrongYearSuspected === true);
      // Odmowa Z uwagami to werdykt walidatora FK — zostawiamy dotychczasowe zachowanie
      // (uwagi na czerwono, wysyłka nadal możliwa, bo ich dry-run jest doradczy).
      // Odmowa BEZ uwag to awaria wywołania — pokazujemy ją wprost i blokujemy wystawienie.
      setFkFailure(
        fkMessages.length === 0
          ? {
              httpStatus: typeof body?.httpStatus === 'number' ? body.httpStatus : undefined,
              raw: typeof body?.raw === 'string' ? body.raw : undefined,
            }
          : null,
      );
      setOpen(true);
      return;
    }
    // Reszta to awarie stanu/łączności — backend przysyła gotowy komunikat po polsku.
    toast.show({
      variant: 'error',
      title:
        typeof body?.message === 'string'
          ? body.message
          : t('tenant.documents.decree.symfoniaSend.genericError'),
    });
  }

  const dryRunMutation = useMutation({
    mutationFn: async () => {
      if (!documentId) throw new Error('NOT_READY');
      const res = await api.post<SendResponse>(`/documents/${documentId}/send-to-symfonia`, {
        dryRun: true,
        // Sprawdzenie na próbę dokumentu już wysłanego też musi przejść — inaczej nie dałoby
        // się zobaczyć uwag przed świadomym ponowieniem.
        allowResend: alreadySent,
        // „Dziś" z zegara i strefy przeglądarki — serwer liczy w UTC, więc sam go nie zna.
        clientDate: localIsoDate(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      resetPanel();
      setMessages(data.messages ?? []);
      setOwnWarnings(data.warnings ?? []);
      setWrongYear(data.wrongYearSuspected === true);
      setOpen(true);
    },
    onError: (err) => {
      resetPanel();
      handleFailure(err);
    },
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!documentId) throw new Error('NOT_READY');
      const res = await api.post<SendResponse>(`/documents/${documentId}/send-to-symfonia`, {
        dryRun: false,
        allowResend: alreadySent,
        clientDate: localIsoDate(),
      });
      return res.data;
    },
    onSuccess: (data) => {
      setOpen(false);
      setResendAccepted(false);
      toast.show({
        variant: 'success',
        title: t('tenant.documents.decree.symfoniaSend.sentToast', {
          number: data.fkDocumentNumber ?? data.fkDocumentId ?? '',
        }),
      });
      // Ślad wysyłki („Wysłano…") przychodzi z GET faktury — bez odświeżenia przycisk
      // wyglądałby, jakby dokument nadal nie poszedł.
      queryClient.invalidateQueries({ queryKey: ['purchase-invoice', documentId] });
      queryClient.invalidateQueries({ queryKey: ['sale-invoice', documentId] });
    },
    onError: (err) => handleFailure(err),
  });

  const errors = messages.filter(isError);
  const warnings = messages.filter(isWarning);
  const infos = messages.filter((m) => !isError(m) && !isWarning(m));
  const blockedByOwnProblems = ownProblems.length > 0;
  const canSend =
    !!documentId &&
    !blockedByOwnProblems &&
    // Wywołanie do FK padło (odmowa bez uwag) — wystawienie i tak by się nie udało, a przycisk
    // dotyczy operacji NIEODWRACALNEJ. Odblokuje je dopiero udane sprawdzenie na próbę.
    !fkFailure &&
    (!alreadySent || resendAccepted) &&
    !sendMutation.isPending;

  return (
    <>
      <div className="flex flex-wrap items-center gap-2" data-testid={testId}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => dryRunMutation.mutate()}
          disabled={!documentId || dryRunMutation.isPending}
          loading={dryRunMutation.isPending}
          data-testid={`${testId}-btn`}
        >
          <Send className="mr-1 h-3.5 w-3.5" />
          {t('tenant.documents.decree.symfoniaSend.button')}
        </Button>
        {alreadySent && showSentStatus && (
          <SymfoniaSentStatus
            sentAt={sentAt}
            sentDocumentNumber={sentDocumentNumber}
            data-testid={`${testId}-sent-badge`}
          />
        )}
      </div>

      <Modal
        open={open}
        onClose={() => {
          setOpen(false);
          setResendAccepted(false);
        }}
        title={t('tenant.documents.decree.symfoniaSend.modalTitle')}
        description={t('tenant.documents.decree.symfoniaSend.modalDescription')}
        size="lg"
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="ghost"
              onClick={() => {
                setOpen(false);
                setResendAccepted(false);
              }}
              data-testid={`${testId}-cancel`}
            >
              {t('common.cancel')}
            </Button>
            <Button
              // Wysyłka jest nieodwracalna — świadomie wariant ostrzegawczy, nie „primary".
              variant="danger"
              onClick={() => sendMutation.mutate()}
              disabled={!canSend}
              loading={sendMutation.isPending}
              data-testid={`${testId}-confirm`}
            >
              {errors.length > 0
                ? t('tenant.documents.decree.symfoniaSend.confirmDespiteErrors')
                : t('tenant.documents.decree.symfoniaSend.confirm')}
            </Button>
          </div>
        }
      >
        <div className="space-y-3 text-sm">
          {blockedByOwnProblems && (
            <div
              className="rounded border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40"
              data-testid={`${testId}-problems`}
            >
              <p className="mb-1 font-semibold text-red-800 dark:text-red-300">
                {t('tenant.documents.decree.symfoniaSend.notReadyTitle')}
              </p>
              <ul className="list-disc space-y-1 pl-5 text-red-800 dark:text-red-300">
                {ownProblems.map((p) => (
                  <li key={`${p.field}-${p.message}`}>{p.message}</li>
                ))}
              </ul>
            </div>
          )}

          {wrongYear && (
            <div
              className="rounded border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
              data-testid={`${testId}-wrong-year`}
            >
              {t('tenant.documents.decree.symfoniaSend.wrongYearHint')}
            </div>
          )}

          {ownWarnings.length > 0 && (
            <ul
              className="list-disc space-y-1 pl-5 text-amber-700 dark:text-amber-300"
              data-testid={`${testId}-own-warnings`}
            >
              {ownWarnings.map((w) => (
                <li key={`${w.field}-${w.message}`}>{w.message}</li>
              ))}
            </ul>
          )}

          {fkFailure && (
            <div
              className="rounded border border-red-300 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/40"
              data-testid={`${testId}-fk-failure`}
            >
              <p className="mb-1 font-semibold text-red-800 dark:text-red-300">
                {t('tenant.documents.decree.symfoniaSend.fkFailureTitle', {
                  status: fkFailure.httpStatus ?? '—',
                })}
              </p>
              <p className="text-red-800 dark:text-red-300">
                {t('tenant.documents.decree.symfoniaSend.fkFailureHint')}
              </p>
              {fkFailure.raw ? (
                <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded bg-red-100 p-2 text-xs text-red-900 dark:bg-red-900/40 dark:text-red-200">
                  {fkFailure.raw}
                </pre>
              ) : null}
            </div>
          )}

          {errors.length === 0 && warnings.length === 0 && !blockedByOwnProblems && !fkFailure && (
            <p
              className="flex items-center gap-2 text-green-700 dark:text-green-400"
              data-testid={`${testId}-clean`}
            >
              <CheckCircle2 className="h-4 w-4" />
              {t('tenant.documents.decree.symfoniaSend.clean')}
            </p>
          )}

          {[
            { rows: errors, key: 'errors', className: 'text-red-700 dark:text-red-300' },
            { rows: warnings, key: 'warnings', className: 'text-amber-700 dark:text-amber-300' },
            { rows: infos, key: 'infos', className: 'text-text-secondary' },
          ].map(({ rows, key, className }) =>
            rows.length === 0 ? null : (
              <ul
                key={key}
                className={`list-none space-y-1 ${className}`}
                data-testid={`${testId}-${key}`}
              >
                {rows.map((m, i) => (
                  <li key={`${key}-${i}`} className="flex items-start gap-2">
                    {key === 'infos' ? (
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                    ) : (
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    )}
                    <span>{m.Message}</span>
                  </li>
                ))}
              </ul>
            ),
          )}

          {alreadySent && !blockedByOwnProblems && (
            <div className="rounded border border-border-primary p-3">
              <Checkbox
                checked={resendAccepted}
                onChange={(e) => setResendAccepted(e.target.checked)}
                label={t('tenant.documents.decree.symfoniaSend.resendAck')}
                data-testid={`${testId}-resend-ack`}
              />
            </div>
          )}
        </div>
      </Modal>
    </>
  );
}
